import React, { useEffect, useMemo, useRef, useState } from "react";
import DynamicComponentsRenderer from "../../../unicomponents/formComponents/DynamicComponentsRenderer";
import LeftMenuSidebar from "../../../unicomponents/LeftMenuSidebar";
import { useReactToPrint } from "react-to-print";
import { useUIContext } from "../../../context/UIContext";
import useComponentsToForms from "../../../hooks/useComponentsToForms";
import useAllForms from "../../../hooks/useAllForms";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuthContext } from "../../../context/AuthContext";
import useCertificateHistory from "../../../hooks/useCertificateHistory";
import { dateToYearMonthDayFormat } from "../../../util/formatdate";
import styles from "./css/OfficialDocuments.module.css";
import useStudentInfo from "../../../hooks/useStudentInfo";

export default function PaOfficialDocuments() {
  const { t } = useTranslation();
  const location = useLocation();
  const paths = location.pathname.split("/");
  const schoolId = paths[1];

  const [studentName, setStudentName] = useState("");
  const [studentUsername, setStudentUsername] = useState("");
  const [keyData, setKeyData] = useState({});
  // const [validateInput, setValidateInput] = useState({});
  const [interComponentData, setInterComponentData] = useState({});
  const [disabled, setDisabled] = useState(false);
  const [beforePrint, setBeforePrint] = useState(false);

  const [activeMenu, setActiveMenu] = useState({
    menu_name: "",
    menu_id: "",
  });

  const conditions = {
    schoolid: schoolId,
    form_category: "parentOfficialDocuments",
  };

  const [formQueryConditions, setFormQueryConditions] = useState({
    schoolid: schoolId,
    form_id: "",
  });

  const [styleSettings, setStyleSettings] = useState({});

  const {
    allFormsQuery: { data: allFormsData },
  } = useAllForms(conditions);

  const {
    componentsToFormsQuery: { data: componentsToFormsData = [], isLoading },
  } = useComponentsToForms(formQueryConditions);

  const graduationCertificateCustomContents = useMemo(() => {
    if (!Array.isArray(componentsToFormsData)) return {};
    const row = componentsToFormsData.find((item) => {
      const name = item?.components?.component_filename;
      return (
        name === "GraduationCertificate.jsx" ||
        (typeof name === "string" && name.includes("GraduationCertificate"))
      );
    });
    const c = row?.customized_contents;
    return c && typeof c === "object" ? c : {};
  }, [componentsToFormsData]);

  const { findStudentInfo } = useStudentInfo();

  const findStudentInfoRef = useRef(findStudentInfo);

  const {
    createCertificateHistory,
    updateCertificateHistory,
    deleteCertificateHistory,
  } = useCertificateHistory();

  const { user, selectedChild } = useAuthContext();

  const { setIsLoadingModal, handleToastCenterTop } = useUIContext();

  useEffect(() => {
    if (allFormsData) {
      setFormQueryConditions((prev) => ({
        ...prev,
        form_id: allFormsData[0].id,
      }));

      setActiveMenu({
        menu_name: allFormsData[0].form_name,
        menu_id: allFormsData[0].id,
        description: allFormsData[0].description,
        form_label: allFormsData[0].form_label,
      });

      setStyleSettings(allFormsData[0]?.options || {});
    }
  }, [allFormsData]);

  useEffect(() => {
    if (selectedChild) {
      findStudentInfoRef.current.mutate(
        {
          username: selectedChild,
          schoolid: schoolId,
        },
        {
          onSuccess: (data) => {
            setStudentName(data.fullname || "");
            setStudentUsername(data.username || "");
            setKeyData(data);
          },
          onError: (error) => {
            console.error(error);
          },
        }
      );
    }
  }, [schoolId, selectedChild]);

  useEffect(() => {
    if (activeMenu?.menu_name !== "graduationCertificate") return;
    if (interComponentData?.schoolStage !== "all") return;
    if (graduationCertificateCustomContents?.schoolStageAll) return;
    setInterComponentData((prev) => ({ ...prev, schoolStage: "" }));
  }, [
    activeMenu?.menu_name,
    graduationCertificateCustomContents?.schoolStageAll,
    interComponentData?.schoolStage,
  ]);

  const handleQueryCondition = (item) => {
    if (item?.id) {
      setFormQueryConditions((prev) => ({
        ...prev,
        form_id: item.id,
      }));

      setStyleSettings(item.options || {});
    }
  };

  const triggerPrint = useReactToPrint({
    content: () => {
      // addPageNumbers();
      return contentToPrint.current;
    },
    documentTitle:
      activeMenu?.description === "student"
        ? `${activeMenu?.form_label}-${keyData?.fullname}`
        : `${activeMenu?.form_label}-${keyData?.school_year}-${keyData?.currentPeriod?.term_name}-${keyData[1]}-${keyData[2]}-${keyData[3]}-${keyData.fullname}`,
    onBeforePrint: () => {
      console.log("before printing...");
    },
    onAfterPrint: () => {
      console.log("after printing...");
    },
    removeAfterPrint: true,
    pageStyle: `@media print {
      .noPrint {
          display: none !important;
        }
      @page {
        size: A4;
        margin: 10mm;
      }


    }`,
  });

  const contentToPrint = useRef(null);
  const handlePrint = () => {
    // console.log("keyData", keyData);

    if (activeMenu?.description === "student" && keyData?.fullname) {
      if (activeMenu?.description === "student") {
        if (
          !interComponentData?.entranceDate &&
          activeMenu?.menu_name === "enrollmentCertificate"
        ) {
          handleToastCenterTop(
            t("admin.certificate.noEnrollmentDate") ||
              "학생 정보에 입학일 기록이 없다면 재학증명서를 발급할 수 없습니다."
          );
          return;
        }

        if (
          (interComponentData?.terminationDate ||
            (interComponentData?.enrollment_status &&
              interComponentData.enrollment_status !== "재학중")) &&
          activeMenu?.menu_name === "enrollmentCertificate"
        ) {
          handleToastCenterTop(
            t("admin.certificate.alreadyTerminatedStudent") ||
              "재학 중이 아닌 학생은 재학증명서를 발급할 수 없습니다."
          );
          return;
        }
      }

      handleUpdateCertificateHistory();
      triggerPrint();
      return;
    }

    if (
      keyData.school_year &&
      keyData[1] &&
      keyData[2] &&
      keyData[3] &&
      keyData.fullname
    ) {
      triggerPrint();
    } else {
      console.log(
        "keyData is incomplete. Please ensure all data is available."
      );
      handleToastCenterTop(t("common.messages.printAfterSelectStudent"));
    }
  };

  //증명서 발급 이력 생성
  const handleCreateCertificateHistory = () => {
    if (!keyData?.fullname || !keyData?.username) {
      handleToastCenterTop("대상을 먼저 선택해 주세요.");
      return;
    }
    if (activeMenu?.description === "student") {
      if (
        !interComponentData?.entranceDate &&
        activeMenu?.menu_name === "enrollmentCertificate"
      ) {
        handleToastCenterTop(
          t("admin.certificate.noEnrollmentDate") ||
            "학생 정보에 입학일 기록이 없다면 재학증명서를 발급할 수 없습니다."
        );
        return;
      }

      if (
        (interComponentData?.terminationDate ||
          (interComponentData?.enrollment_status &&
            interComponentData.enrollment_status !== "재학중")) &&
        activeMenu?.menu_name === "enrollmentCertificate"
      ) {
        handleToastCenterTop(
          t("admin.certificate.alreadyTerminatedStudent") ||
            "재학 중이 아닌 학생은 재학증명서를 발급할 수 없습니다."
        );
        return;
      }

      if (
        activeMenu?.menu_name === "graduationCertificate" &&
        interComponentData?.canIssueGraduationCertificate !== true
      ) {
        const reasons =
          interComponentData?.graduationCertificateMissingReasons;
        const msg =
          Array.isArray(reasons) && reasons.length > 0
            ? `${reasons.join(" ")} 졸업증명서를 발급할 수 없습니다.`
            : "졸업증명서를 발급할 수 없습니다.";
        handleToastCenterTop(msg);
        return;
      }
    }

    setDisabled(true);
    setIsLoadingModal(true);
    setBeforePrint(true);
    setInterComponentData((prev) => ({
      ...prev,
      beforePrint: true,
    }));

    const certificateHistory = {
      schoolid: schoolId,
      certificate_type: activeMenu?.menu_name,
      subject_type: activeMenu?.description,
      subject_username: keyData?.username,
      request_date: dateToYearMonthDayFormat(new Date()),
      issuer: user?.username,
      purpose: interComponentData?.purpose || "",
      snapshot: interComponentData?.snapshot,
      ...(interComponentData?.certificateNoTitle && {
        certificateNoTitle: interComponentData.certificateNoTitle,
      }),
    };

    createCertificateHistory.mutate(certificateHistory, {
      onSuccess: (certificateData) => {
        setInterComponentData((prev) => ({
          ...prev,
          certificate_no: certificateData?.certificate_no || "",
          certificate_id: certificateData?.id || "",
        }));
        setTimeout(() => {
          setDisabled(false);
          setIsLoadingModal(false);
        }, 1000);
      },
      onError: (error) => {
        console.error("Error creating certificateHistory:", error);
        setDisabled(false);
        setIsLoadingModal(false);
      },
    });
  };

  //증명서 이력 업데이트
  const handleUpdateCertificateHistory = () => {
    if (!interComponentData?.certificate_id) {
      handleToastCenterTop(
        t("admin.certificate.noRequest") ||
          "발급신청한 것이 없습니다. 새로고침 후 발급신청을 먼저 해주세요."
      );
      return;
    }

    setDisabled(true);
    setIsLoadingModal(true);

    const certificateHistory = {
      id: interComponentData?.certificate_id,
      status: "completed",
    };

    updateCertificateHistory.mutate(certificateHistory, {
      onSuccess: () => {
        setTimeout(() => {
          setDisabled(false);
          setIsLoadingModal(false);
          setBeforePrint(false);
          setInterComponentData((prev) => ({
            ...prev,
            certificate_no: "",
            certificate_id: "",
          }));
        }, 3000);
      },
      onError: (error) => {
        console.error(error);
        setDisabled(false);
        setIsLoadingModal(false);
        handleToastCenterTop(t("common.messages.errorAlert") + error.message);
      },
    });
  };

  //증명서 이력 삭제
  const handleDeleteCertificateHistory = () => {
    if (!interComponentData?.certificate_id) {
      handleToastCenterTop("발급이력이 없습니다.");
      return;
    }

    setDisabled(true);
    setIsLoadingModal(true);

    deleteCertificateHistory.mutate(interComponentData?.certificate_id, {
      onSuccess: () => {
        setTimeout(() => {
          setDisabled(false);
          setIsLoadingModal(false);
          setBeforePrint(false);
          setInterComponentData((prev) => ({
            ...prev,
            certificate_no: "",
            certificate_id: "",
          }));
        }, 1000);
      },
      onError: (error) => {
        console.error(error);
        setDisabled(false);
        setIsLoadingModal(false);
        handleToastCenterTop(t("common.messages.errorAlert") + error.message);
      },
    });
  };

  const handleSelectSchoolStage = (e) => {
    setInterComponentData((prev) => ({
      ...prev,
      schoolStage: e.target.value,
    }));
  };

  return (
    <div className="basicWidth">
      <div className="container-fluid">
        <div className="row">
          <div className="col-12 col-md-2">
            <LeftMenuSidebar
              activeMenu={activeMenu}
              setActiveMenu={setActiveMenu}
              menuData={allFormsData}
              extraFunction={handleQueryCondition}
              itemName="form_name"
              itemLabel="form_label"
            />
          </div>
          <div className="col-12 col-md-10">
            <div className="d-flex justify-content-end flex-wrap mt-2">
              <div>
                <label>{t("common.user.student") || "학생"}</label>
                <input
                  type="text"
                  id="searchKeyword"
                  name="searchKeyword"
                  className="form-control"
                  placeholder={t("common.user.student") || "학생"}
                  value={studentName || ""}
                  disabled={true}
                />
              </div>
              <div className="ms-3">
                <label>{t("common.user.student") || "학생"} ID</label>
                <input
                  type="text"
                  className="form-control"
                  name="username"
                  placeholder={t("common.user.username") || "학생아이디"}
                  value={studentUsername || ""}
                  disabled={true}
                />
              </div>
            </div>
            <div className="d-flex justify-content-end flex-wrap mb-3 mt-2">
              {activeMenu?.menu_name?.includes("graduation") && (
                <div
                  style={{ width: "160px", paddingTop: "2px" }}
                  className="text-center"
                >
                  <select
                    className="form-control mt-1"
                    name="schoolStage"
                    onChange={handleSelectSchoolStage}
                    value={interComponentData?.schoolStage || ""}
                    disabled={beforePrint}
                  >
                    <option value="">과정선택</option>
                    {graduationCertificateCustomContents?.schoolStageAll && (
                      <option value="all">전체</option>
                    )}
                    <option value="elementary">초등</option>
                    <option value="middle">중등</option>
                    <option value="high">고등</option>
                  </select>
                </div>
              )}
              {beforePrint && (
                <div style={{ width: "160px" }} className="text-center">
                  <button
                    className="btn btn-success"
                    type="button"
                    onClick={() => {
                      handlePrint(null, () => contentToPrint.current);
                    }}
                    // disabled={modifyMode}
                  >
                    인쇄로 발급확정
                  </button>
                </div>
              )}
              <div style={{ width: "100px" }} className="me-1 text-center">
                {!beforePrint && (
                  <button
                    className="btn btn-primary"
                    type="button"
                    onClick={() => {
                      handleCreateCertificateHistory();
                    }}
                    disabled={
                      disabled ||
                      (activeMenu?.menu_name === "graduationCertificate" &&
                        interComponentData?.canIssueGraduationCertificate !==
                          true)
                    }
                  >
                    {t("admin.certificate.request") || "발급신청"}
                  </button>
                )}
                {beforePrint && (
                  <button
                    className="btn btn-warning"
                    type="button"
                    onClick={() => {
                      handleDeleteCertificateHistory();
                    }}
                    disabled={disabled}
                  >
                    발급취소
                  </button>
                )}
              </div>
            </div>
            <div
              id="officialDocumentPrintArea"
              className="d-flex justify-content-center print-page"
              ref={contentToPrint}
            >
              <div className="" style={{ width: "210mm" }}>
                {/* 워터마크 */}
                {activeMenu?.menu_name?.includes("Certificate") && (
                  <div className={styles.watermark}>
                    {t("admin.certificate.preview") || "미리보기"}
                  </div>
                )}

                <div
                  colSpan="1"
                  className="p-2"
                  style={{
                    maxWidth: "200mm",
                    fontFamily: styleSettings.fontFamily
                      ? styleSettings.fontFamily
                      : "",
                  }}
                >
                  {styleSettings?.outLine &&
                  Object.keys(styleSettings.outLine).length > 0 ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <div
                        style={{
                          width: "190mm",
                          height: "240mm",
                          maxWidth: "100%",
                          margin: "70px 70px 50px 70px",
                          ...styleSettings.outLine,
                        }}
                      >
                        {!isLoading &&
                          componentsToFormsData &&
                          componentsToFormsData.map((item, index) => (
                            <DynamicComponentsRenderer
                              components={item.components}
                              customizedContents={item.customized_contents}
                              keyData={keyData}
                              setIsLoadingModal={setIsLoadingModal}
                              key={`${item.id}-${item.order}-${index}`}
                              componentsToFormId={item.id}
                              styleSettings={styleSettings}
                              interComponentData={interComponentData}
                              setInterComponentData={setInterComponentData}
                            />
                          ))}
                      </div>
                    </div>
                  ) : (
                    !isLoading &&
                    componentsToFormsData &&
                    componentsToFormsData.map((item, index) => (
                      <DynamicComponentsRenderer
                        components={item.components}
                        customizedContents={item.customized_contents}
                        keyData={keyData}
                        setIsLoadingModal={setIsLoadingModal}
                        key={`${item.id}-${item.order}-${index}`}
                        componentsToFormId={item.id}
                        styleSettings={styleSettings}
                        interComponentData={interComponentData}
                        setInterComponentData={setInterComponentData}
                      />
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
